import * as Yup from 'yup';
import { useState } from 'react';
import { useFormik } from 'formik';

import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  Checkbox,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
} from '@mui/material';

import useLocales from 'src/hooks/useLocales';
import useNotifications from 'src/hooks/useNotifications';

import eventBus, { EventTypes } from 'src/utils/eventBus';

import { useSelector } from 'src/redux/store';
import documentService from 'src/services/documentService';
import { DocumentReject } from 'src/@types/documents/types';
import { increaseDocumentReloadState } from 'src/redux/slices/document';

import DocumentActionMessageField, {
  DOCUMENT_ACTION_MESSAGE_MAX_LENGTH,
} from './DocumentActionMessageField';

type DocumentRejectionDialogProps = {
  handleClose: (rejected: boolean) => void;
};

export default function DocumentRejectionDialog({ handleClose }: DocumentRejectionDialogProps) {
  const { t } = useLocales();
  const { document } = useSelector((state) => state.document);
  const { enqueueSuccess } = useNotifications();
  const [addMessageChecked, setAddMessageChecked] = useState(true);

  const validationSchema = Yup.object().shape({
    message: Yup.string().max(
      DOCUMENT_ACTION_MESSAGE_MAX_LENGTH,
      t('shared.errors.maximumValueExceeded')
    ),
  });

  const handleSubmit = async () => {
    if (!document || !formik.isValid) {
      return;
    }

    try {
      formik.setSubmitting(true);
      const request = {
        id: document.id,
        rejectionCustomComment: formik.values.message,
      } as DocumentReject;
      await documentService.rejectDocument(request);
      enqueueSuccess('documents.snackbar.rejected');
      increaseDocumentReloadState();
      eventBus.publish({
        topic: EventTypes.DocumentRejected,
        payload: {},
      });
      handleClose(true);
    } catch (error) {
      formik.setErrors(t(error));
    } finally {
      formik.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      message: '',
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const onClose = () => {
    formik.resetForm();
    handleClose(false);
  };

  const onAddMessageCheckedChange = () => {
    setAddMessageChecked((x) => !x);
    if (addMessageChecked) {
      formik.setFieldValue('message', '');
    }
  };

  return (
    <Dialog fullWidth open onClose={() => onClose()}>
      <DialogTitle>{t('documents.rejection')}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }} variant="body1">
          {t('documents.areYouSureWantToReject')}
        </Typography>

        <FormControlLabel
          label={<>{t('documents.addMessage')}</>}
          control={
            <Checkbox
              checked={addMessageChecked}
              onChange={onAddMessageCheckedChange}
              id="7sul5zr9"
            />
          }
        />

        <DocumentActionMessageField
          {...formik.getFieldProps('message')}
          disabled={formik.isSubmitting || !addMessageChecked}
          error={formik.touched.message ? formik.errors.message : undefined}
          id="ckd16u7w"
        />
      </DialogContent>
      <DialogActions>
        <Button
          disabled={formik.isSubmitting}
          onClick={onClose}
          type="button"
          variant="outlined"
          color="inherit"
          id="mz5nnxqa"
        >
          {t('shared.buttons.cancel')}
        </Button>
        <LoadingButton
          disabled={!formik.isValid}
          loading={formik.isSubmitting}
          onClick={() => handleSubmit()}
          type="submit"
          variant="contained"
          id="fvlqw9v1"
        >
          {t('documents.buttons.reject')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
